import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Trash2, Download, Smartphone, Instagram, Youtube, X, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


/* ─────────────────────────────────────────────────────────────────────────
   Composer Presets — Save current layer / camera, Apply existing
   ──────────────────────────────────────────────────────────────────────── */

export const PresetSaveLoad = ({ scope, currentPayload, onApply }) => {
  const { token } = useAuth();
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [items, setItems] = useState([]);
  const [showSave, setShowSave] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    axios.get(`${API}/presets/composer?scope=${scope}`, { headers: auth })
      .then(r => setItems(r.data.items || []))
      .catch(() => {});
  }, [scope]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/presets/composer`, {
        name: name.trim(), scope, payload: currentPayload,
      }, { headers: auth });
      setItems((prev) => [r.data.preset, ...prev]);
      setShowSave(false);
      setName("");
      toast.success("Preset saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this preset?")) return;
    try {
      await axios.delete(`${API}/presets/composer/${id}`, { headers: auth });
      setItems((prev) => prev.filter(p => p.id !== id));
    } catch (e) {
      toast.error("Could not delete preset");
    }
  };

  return (
    <div className="flex flex-wrap gap-1.5 items-center" data-testid={`preset-row-${scope}`}>
      <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-400 pr-1">
        Presets
      </span>
      {items.map((p) => (
        <span key={p.id} className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={() => onApply?.(p.payload)}
            data-testid={`preset-apply-${p.id}`}
            className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-white border border-ink-200 hover:border-blue-500"
          >
            {p.name}
          </button>
          <button
            type="button"
            onClick={() => remove(p.id)}
            data-testid={`preset-delete-${p.id}`}
            className="text-rose-400 hover:text-rose-600"
            title="Delete"
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      {!showSave ? (
        <button
          type="button"
          onClick={() => setShowSave(true)}
          data-testid={`preset-save-open-${scope}`}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-500 text-white hover:bg-blue-600"
        >
          <Save className="w-3 h-3" /> Save as preset
        </button>
      ) : (
        <span className="inline-flex items-center gap-1">
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 60))}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setShowSave(false); }}
            placeholder="Preset name…"
            data-testid={`preset-save-input-${scope}`}
            className="px-2 py-1 text-[11px] rounded-full border border-ink-200 focus:outline-none focus:border-blue-500"
          />
          <Button size="sm" disabled={saving || !name.trim()} onClick={save}
                  data-testid={`preset-save-submit-${scope}`}
                  className="h-6 px-2 text-[11px] bg-blue-500 hover:bg-blue-600 text-white">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
          </Button>
          <button type="button" onClick={() => setShowSave(false)} className="text-ink-400 hover:text-ink-950">
            <X className="w-3 h-3" />
          </button>
        </span>
      )}
    </div>
  );
};


/* ─────────────────────────────────────────────────────────────────────────
   Export-to-platform button (TikTok / IG / YouTube)
   ──────────────────────────────────────────────────────────────────────── */

const TARGET_ICONS = {
  tiktok:      { icon: Smartphone, label: "TikTok",       color: "bg-black" },
  ig_reel:     { icon: Instagram,  label: "IG Reel",      color: "bg-fuchsia-500" },
  ig_square:   { icon: Instagram,  label: "IG Square",    color: "bg-fuchsia-400" },
  ig_portrait: { icon: Instagram,  label: "IG Portrait",  color: "bg-fuchsia-500" },
  yt_short:    { icon: Youtube,    label: "YT Short",     color: "bg-rose-600" },
  yt_wide:     { icon: Youtube,    label: "YT Wide",      color: "bg-rose-500" },
};


export const ExportToPlatformButton = ({ sourceCollection, sourceId, testIdPrefix = "export" }) => {
  const { token } = useAuth();
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [open, setOpen] = useState(false);
  const [jobs, setJobs] = useState({});  // {target: job}
  const [submitting, setSubmitting] = useState(null);

  const queue = async (target) => {
    setSubmitting(target);
    try {
      const r = await axios.post(`${API}/presets/exports`, {
        source_collection: sourceCollection,
        source_id: sourceId,
        target,
      }, { headers: auth });
      setJobs((prev) => ({ ...prev, [target]: r.data.job }));
      toast.success(`${TARGET_ICONS[target]?.label || target} queued`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not queue export");
    } finally {
      setSubmitting(null);
    }
  };

  // Poll status for queued jobs every 8s
  useEffect(() => {
    const queuedIds = Object.values(jobs).filter(j => j && (j.status === "queued" || j.status === "rendering")).map(j => j.id);
    if (queuedIds.length === 0) return undefined;
    const interval = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/presets/exports?limit=40`, { headers: auth });
        const items = r.data.items || [];
        setJobs((prev) => {
          const next = { ...prev };
          items.forEach(j => {
            if (next[j.target] && next[j.target].id === j.id) next[j.target] = j;
          });
          return next;
        });
      } catch (_e) { /* silent */ }
    }, 8000);
    return () => clearInterval(interval);
  }, [jobs, auth]);

  return (
    <>
      <Button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${testIdPrefix}-button`}
        className="bg-ink-950 hover:bg-ink-800 text-white"
      >
        <Download className="w-4 h-4 mr-1" /> Export
      </Button>

      {open && (
        <div
          data-testid={`${testIdPrefix}-modal`}
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="bg-white rounded-3xl w-full max-w-md p-5 space-y-3 shadow-2xl">
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500">
                Export to platform
              </p>
              <button type="button" onClick={() => setOpen(false)} className="text-ink-400 hover:text-ink-950">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-ink-500">
              We recode your mp4 to the platform's exact aspect + bitrate spec.
              Free renders include the watermark.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(TARGET_ICONS).map(([id, info]) => {
                const job = jobs[id];
                const Icon = info.icon;
                const completed = job?.status === "completed";
                const inFlight = job && (job.status === "queued" || job.status === "rendering");
                return (
                  <div key={id} className="rounded-2xl border border-ink-200 p-3 flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <span className={`w-7 h-7 rounded-lg ${info.color} text-white flex items-center justify-center shrink-0`}>
                        <Icon className="w-4 h-4" />
                      </span>
                      <p className="text-sm font-bold text-ink-950">{info.label}</p>
                    </div>
                    {completed ? (
                      <a
                        href={job.result_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`${testIdPrefix}-${id}-download`}
                        className="text-[11px] font-bold text-emerald-700 underline inline-flex items-center gap-1"
                      >
                        Download <ArrowRight className="w-3 h-3" />
                      </a>
                    ) : inFlight ? (
                      <div className="flex items-center gap-1.5">
                        <Loader2 className="w-3 h-3 animate-spin text-amber-500" />
                        <span className="text-[10px] font-bold text-amber-700 tabular-nums">
                          {job.progress || 0}%
                        </span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => queue(id)}
                        disabled={submitting === id}
                        data-testid={`${testIdPrefix}-${id}-queue`}
                        className="text-[11px] font-bold text-blue-500 hover:underline self-start"
                      >
                        {submitting === id ? "queueing…" : "Recode →"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};


/* ─────────────────────────────────────────────────────────────────────────
   OKX-style onboarding tour
   ──────────────────────────────────────────────────────────────────────── */

const TOUR_STEPS = [
  {
    title: "Welcome to XEA",
    body: "The creative OS for marketing creatives, designer assets and CGI video. Let's spin through what you can do in 60 seconds.",
    accent: "from-coral-500 to-orange-400",
  },
  {
    title: "Three modes, one canvas",
    body: "Tap the XEA logo (top-left) anytime to switch between Marketing, Designer and CGI Studio. The background colour shifts to match.",
    accent: "from-coral-500 via-violet-500 to-blue-500",
    hint: "Look for the logo — it's your mode picker.",
  },
  {
    title: "Marketing mode 🍑",
    body: "Ship social creatives with a brief: hook lines, visuals, the works. AI handles copy + image in one call.",
    accent: "from-coral-500 to-coral-700",
  },
  {
    title: "Designer mode 💜",
    body: "Pure prompt-to-asset. Logos, illustrations, mockups — no marketing fluff, just the visual you described.",
    accent: "from-violet-500 to-fuchsia-500",
  },
  {
    title: "CGI Studio 💎",
    body: "Apply AI filters, frame-by-frame stylization (Kling 1.6 Pro), and even build multi-clip timelines or AE-lite compositions with text + stickers + camera moves.",
    accent: "from-blue-500 to-cyan-500",
  },
  {
    title: "Publish, follow, earn",
    body: "Every render can ship to the shared Community feed in one click. Get tipped in XEA tokens, climb the Weekend Top-20 leaderboard, build a following at /creator/your_handle.",
    accent: "from-emerald-500 to-blue-500",
    cta: "Let's go 🚀",
  },
];


export const OnboardingTour = () => {
  const { token, user } = useAuth();
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!token) return;
    const key = `xea_onboarded_${user?.id || "anon"}`;
    const seen = localStorage.getItem(key);
    if (!seen) {
      const t = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [token, user?.id]);

  const close = () => {
    const key = `xea_onboarded_${user?.id || "anon"}`;
    localStorage.setItem(key, new Date().toISOString());
    setOpen(false);
    setStep(0);
  };

  if (!open) return null;
  const s = TOUR_STEPS[step];
  const isLast = step === TOUR_STEPS.length - 1;

  return (
    <div
      data-testid="onboarding-tour"
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl">
        <div className={`h-32 bg-gradient-to-br ${s.accent} flex items-center justify-center relative`}>
          <Sparkles className="w-12 h-12 text-white drop-shadow" />
          <button
            type="button"
            onClick={close}
            data-testid="onboarding-close"
            className="absolute top-3 right-3 bg-black/30 hover:bg-black/50 text-white rounded-full p-1.5"
          >
            <X className="w-4 h-4" />
          </button>
          <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.18em] font-bold text-white/90 bg-white/15 backdrop-blur px-2 py-1 rounded-full">
            {step + 1} / {TOUR_STEPS.length}
          </span>
        </div>
        <div className="p-6 space-y-4">
          <h3 className="text-2xl font-black text-ink-950 tracking-tight">{s.title}</h3>
          <p className="text-sm text-ink-700 leading-relaxed">{s.body}</p>
          {s.hint && (
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-blue-500">{s.hint}</p>
          )}
          {/* Dots */}
          <div className="flex justify-center gap-1.5 pt-2">
            {TOUR_STEPS.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  i === step ? "bg-ink-950 w-6" : "bg-ink-200"
                }`}
              />
            ))}
          </div>
          <div className="flex justify-between gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={close}
              data-testid="onboarding-skip"
              className="text-ink-500"
            >
              Skip
            </Button>
            <Button
              onClick={() => isLast ? close() : setStep(step + 1)}
              data-testid={isLast ? "onboarding-finish" : "onboarding-next"}
              className="bg-ink-950 hover:bg-ink-800 text-white"
            >
              {isLast ? (s.cta || "Done") : "Next"} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
